import { createRouter, createWebHistory } from 'vue-router'
import ControlPage from '../pages/ControlPage.vue'
import ControlList from '../pages/ControlList.vue'
import SettingsPage from '../pages/SettingsPage.vue'
import LoginPage from '../pages/LoginPage.vue'
import CountdownPage from '../pages/CountdownPage.vue'
import CountdownSetup from '../pages/CountdownSetup.vue'
import SessionHistory from '../views/SessionHistory.vue'
import SessionDetail from '../views/SessionDetail.vue'
import SentryTestPage from '../pages/SentryTestPage.vue'
import type { WatchRole } from '@/schemas'
import type { SessionID } from '@/stores/ws'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: ControlPage, // Handle UUID control links at root
      meta: { requiresSession: true, role: 'controller' },
    },
    {
      path: '/countdown/setup',
      name: 'countdown-setup',
      component: CountdownSetup,
      meta: { requiresAuth: true },
    },
    {
      path: '/countdown',
      name: 'countdown',
      component: CountdownPage,
      meta: { requiresSession: false, role: 'owner' as WatchRole }, // Personal countdown, no UUID needed
    },
    {
      path: '/control-list',
      name: 'control-list',
      component: ControlList,
      meta: { requiresAuth: true },
    },
    {
      path: '/control',
      name: 'control',
      component: ControlPage,
      meta: { requiresSession: true, role: 'controller' as WatchRole },
    },
    {
      path: '/settings',
      name: 'settings',
      component: SettingsPage,
    },
    {
      path: '/sessions',
      name: 'sessions',
      component: SessionHistory,
      meta: { requiresAuth: true },
    },
    {
      path: '/session/:uuid',
      name: 'session-detail',
      component: SessionDetail,
      meta: { requiresAuth: false }, // Public access via UUID
    },
    {
      path: '/login',
      name: 'login',
      component: LoginPage,
    },
    {
      // Not linked from nav on purpose, see SentryTestPage.vue.
      path: '/debug/sentry-test',
      name: 'sentry-test',
      component: SentryTestPage,
    },
  ],
})

// Navigation guards for persistent WebSocket with watch/unwatch
router.beforeEach(async (to, from) => {
  // Import WS store dynamically to avoid circular dependency
  const { useWsStore } = await import('@/stores/ws')
  const ws = useWsStore()

  const fromSessionId = from.query.sessionId ? Number(from.query.sessionId) : null
  const toSessionId = to.query.sessionId ? Number(to.query.sessionId) : null

  // Handle leaving a session-based page
  if (from.meta?.requiresSession && fromSessionId) {
    // If we're going to a different session or non-session page, unwatch
    if (!to.meta?.requiresSession || fromSessionId !== toSessionId) {
      ws.stopActivityTracking(fromSessionId as SessionID)
    }
  }

  return true
})

router.afterEach(async (to) => {
  // Import WS store dynamically to avoid circular dependency
  const { useWsStore } = await import('@/stores/ws')
  const ws = useWsStore()

  const toSessionId = to.query.sessionId ? (Number(to.query.sessionId) as SessionID) : null

  // Handle entering a session-based page
  if (to.meta?.requiresSession && toSessionId && to.meta.role) {
    ws.startSession(toSessionId, to.meta.role as WatchRole)
    ws.startActivityTracking(toSessionId)
  }
})

export default router
